import { ref } from 'vue'

export interface LightboxImage {
  src: string
  fallbackSrc?: string
  title?: string
  alt?: string
  [key: string]: unknown
}

export type LightboxSource = string | LightboxImage

const normalizeSource = (source: LightboxSource): LightboxImage | null => {
  if (typeof source === 'string') {
    const src = source.trim()
    return src ? { src } : null
  }
  if (!source || typeof source !== 'object') {
    return null
  }
  const src = typeof source.src === 'string' ? source.src.trim() : ''
  if (!src) {
    return null
  }
  const normalized: LightboxImage = { ...source, src }
  if (typeof normalized.title === 'string') {
    normalized.title = normalized.title.trim()
  }
  if (typeof normalized.alt === 'string') {
    normalized.alt = normalized.alt.trim()
  }
  return normalized
}

export function useImageLightbox() {
  const visible = ref(false)
  const sources = ref<LightboxImage[]>([])
  const index = ref(0)

  const open = (nextSources: LightboxSource[], sourceIndex = 0) => {
    const resolvedSources = nextSources
      .map(source => normalizeSource(source))
      .filter((source): source is LightboxImage => Boolean(source))
    if (!resolvedSources.length) {
      return
    }
    sources.value = resolvedSources
    const targetIndex = Number.isFinite(sourceIndex) ? sourceIndex : 0
    index.value = Math.min(Math.max(targetIndex, 0), resolvedSources.length - 1)
    visible.value = true
  }

  const close = () => {
    visible.value = false
  }

  return {
    visible,
    sources,
    index,
    open,
    close
  }
}
